import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import styled from "styled-components"
import NextButton from "../../components/next-button";
import Network, { GetLibraryResponse } from "../../modules/network";
// import RichTextEditor from "./rich-text-form";
import UploadImageForm from "./upload-image-form";

const RichTextEditor = lazy(() => import('./rich-text-form'));

type Props = {
    library: GetLibraryResponse;
}

const Container = styled.div`
    font-size: 16px;

    .form-section-header {
        margin: 0 0 1.5em;
        padding: 0 0 .5em;
        text-align: center;
    }

    .form-section {
        margin: 1em auto;
        border: 1px solid #d9d9d9;
        border-radius: 1em;
        padding: 1em;

        box-shadow: 0 0 1em rgba(0,0,0,.8);
    }

    .form-subsection {
        background: #f0f0f0;
        padding: .5em;
        margin: .5em;
    }

    .form-section-info {
        margin: .5em 0;
        font-weight: bold;
    }

    .form-section-info-small {
        margin: .5em 0;
        font-weight: bold;
    }

    .editor1 {
        background: white;
    }

    .editor2 {
        background: white;
        height: 20em;
        position: relative;
    }

    .image-form {
        background: white;
        padding: .5em;

        img {
            width: 100%;
        }
    }

    .sections {
        display: flex;
    }

    .form-address {
        textarea {
            width: 90%;
            min-height: 10em;
        }
    }

    .form-email {
        input {
            width: 90%;
        }
    }

    .message {
        background: white;
        padding: 2em;
        border: 1px solid black;
        margin: 1em 0;
        text-align: center;
        font-size: 1.5em;
    }

    .success-message {
        color: black;
    }

    .error-message {
        color: red;
    }

`;

const LibraryForm: React.FC<Props> = ({ library }) => {


    const queryClient = useQueryClient();

    const { mutate, error, isSuccess, isLoading } = useMutation(Network.editLibrary, {
        onSuccess() {
            queryClient.invalidateQueries(['library', library.code]);
        }
    });
    const formMethods = useForm();
    const { register, handleSubmit } = formMethods;
    const [imageMode, setImageMode] = useState<'preview' | 'changeImage' | 'addImage'>(library.imageId ? 'preview' : 'addImage');

    useEffect(() => {
        setImageMode(library.imageId ? 'preview' : 'addImage');
    }, [library]);

    const onSubmit = useCallback((data) => {
        const formData = new FormData();

        formData.append('code', library.code);
        formData.append('intro', JSON.stringify(data.intro));
        formData.append('outro', JSON.stringify(data.outro));
        formData.append('address', data.address || '');
        formData.append('email', data.email || '');

        if (imageMode === 'preview') {
            formData.append('imageId', data.imageId);
        } else {
            formData.append('imageId', '0');
            formData.append('image', data.image[0]);
        }

        mutate(formData);
    }, [mutate, library, imageMode]);

    const introDelta = useMemo(() => {
        try {
            return JSON.parse(library?.intro || '[]');
        } catch (e) {

        }

        return { ops: [] };
    }, [library]);

    const outroDelta = useMemo(() => {
        try {
            return JSON.parse(library?.outro || '[]');
        } catch (e) {

        }

        return { ops: [] };
    }, [library]);

    return <FormProvider {...formMethods}>
        {library && <form onSubmit={handleSubmit(onSubmit)}>
            <Suspense fallback={<div>Ładownie...</div>}>
                <Container>

                    <div className="form-section">
                        <h3 className='form-section-header'>Dane biblioteki</h3>
                        <div className="sections">
                            <div className="form-subsection form-address" style={{ flex: '1 1 65%' }}>
                                <p className="form-section-info">
                                    Adres biblioteki bedzie prezentowany graczom
                                </p>
                                <textarea placeholder="Adres bbilioteki..." defaultValue={library.address} {...register('address')}></textarea>
                            </div>
                            <div className="form-subsection form-email" style={{ flex: '0 0 30%' }}>
                                <p className="form-section-info">adres e-mail biblioteki, który zostanie udostępniony graczom.<br /> UWAGA: Ten adres będzie się wyświetlał przy zadaniach, w których gracze są proszeni o przesyłanie efektów swoich działań.</p>
                                <input placeholder="Adres email biblioteki..." type="text" defaultValue={library.email} {...register('email')} />
                            </div>
                        </div>
                    </div>
                    <div className='form-section'>
                        <h3 className='form-section-header'>Przywitanie</h3>
                        <div className="sections">
                            <div className='form-subsection' style={{ flex: '1 1 65%' }}>
                                <p className='form-section-info'>Ten tekst, skierowany do członków Twojej biblioteki,  będzie się pojawiał na ekranie powitalnym gry</p>
                                <RichTextEditor name="intro" value={introDelta.ops} />
                            </div>
                            <div className='form-subsection' style={{ flex: '0 0 30%' }}>
                                <p className='form-section-info'>To zdjęcie będzie się wyświetlało nad treścią powitania.</p>
                                <p className='form-section-info-mini'>Plik musi być w formacie: JPG, PNG, GIF lub BMP</p>
                                <p className='form-section-info-mini'>Zdjęcie musi być mniejsze niż 500KB</p>

                                <div className="image-form">
                                    {
                                        (imageMode === 'preview') && <div>
                                            <h3>Wgrany obrazek</h3>
                                            <div>
                                                <input type="hidden" {...register('imageId')} />
                                                <img src={library.imageUrl} alt={library.imageAlt} />
                                            </div>
                                            <button onClick={() => { setImageMode('changeImage') }}>Zmień obrazek</button>
                                        </div>
                                    }
                                    {imageMode === 'changeImage' && <div>
                                        <UploadImageForm />
                                        <button onClick={() => { setImageMode('preview') }}>Anuluj</button>
                                    </div>}
                                    {imageMode === 'addImage' && <div>
                                        <UploadImageForm />
                                    </div>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='form-section'>
                        <h3 className='form-section-header'>Pożegnanie</h3>
                        <div className="sections">
                            <div className='form-subsection' style={{ flex: '1 1 65%' }}>
                                <p className='form-section-info'>Ten tekst, skierowany do członków Twojej biblioteki, będzie się pojawiał na ekranie po ukończeniu gry</p>
                                <RichTextEditor name="outro" value={outroDelta.ops} />
                            </div>
                        </div>
                    </div>

                    <div className="messages">
                        {error && <ErrorMessage code={(error as any).code || 0} />}
                        {isSuccess && <p className="message success-message">Zapisano!</p>}
                    </div>

                </Container>
                <div className="post-page-box-links">
                    {!isLoading && <NextButton>Zapisz</NextButton>}
                </div>

            </Suspense>
        </form >}

    </FormProvider >
};

const ErrorMessage: React.FC<{ code: number }> = ({ code }) => {
    return useMemo(() => {
        if (code === 10200) {
            return <p className="message error-message">Termin wprowadzania zmian minął. Jeżeli potrzebują Państwo pomocy, prosimy o kontakt z koordynatorem akcji Noc Bibliotek 2021</p>
        }

        return <p className="message error-message">Błąd zapisu</p>
    }, [code])
}

export default LibraryForm

